import {
  CONVERSATION_INDEX_KEY,
  CONVERSATION_KEY_PREFIX,
  LEGACY_MESSAGES_KEY,
  TITLE_MAX_LENGTH,
} from "@/lib/constants";
import type { ChatMessage } from "@/hooks/use-chat-stream";

/**
 * The local multi-conversation store: pure functions over a StorageLike. No React, no router.
 *
 * Two rules govern everything below:
 *
 * 1. **Reads never throw and never report failure.** A parse error, an absent key or an unusable
 *    localStorage reads as `null` / `[]`. **Writes never throw** either — they return a StoreResult
 *    and the UI ignores a failure, so the chat continues in memory for the session exactly as it
 *    did before this store existed.
 * 2. **The index is the sole record of existence.** `saveConversation` reads the index *first* and
 *    refuses an id it does not carry, before writing anything — which is what makes a late stream
 *    frame unable to resurrect a deleted conversation.
 */
export const CONVERSATION_STORE_VERSION = 1;

/** Landing signals, snake_case to match the API request body and the persisted envelope. */
export interface TrafficContext {
  source_key?: string;
  source_partner?: string;
  referrer?: string;
  utm_source?: string;
  utm_campaign?: string;
}

export interface ConversationMeta {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface ConversationIndex {
  version: number;
  conversations: ConversationMeta[];
}

export interface ConversationEnvelope {
  version: number;
  messages: ChatMessage[];
  traffic_context: TrafficContext;
}

export type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export type StoreResult = { ok: true } | { ok: false; error: string };

export type MigrationResult =
  // `recovered` carries the validated legacy transcript when the messages were readable but could
  // not be persisted (a full quota, a disabled store). AC 3 says no messages are lost and the epic's
  // storage-failure decision says the chat continues in-memory for the session, so the caller opens
  // these in memory and the legacy key stays put for a later retry. Absent when there was nothing
  // readable to recover.
  | { migrated: false; recovered?: ChatMessage[] }
  | { migrated: true; id: string; legacy_cleared: boolean };

function failure(error: unknown): { ok: false; error: string } {
  return { ok: false, error: error instanceof Error ? error.message : String(error) };
}

function conversationKey(id: string): string {
  return `${CONVERSATION_KEY_PREFIX}${id}`;
}

function emptyIndex(): ConversationIndex {
  return { version: CONVERSATION_STORE_VERSION, conversations: [] };
}

/** Never throws: an unreadable or unparseable index is an empty one. */
function readIndex(storage: StorageLike): ConversationIndex {
  try {
    const raw = storage.getItem(CONVERSATION_INDEX_KEY);
    if (!raw) return emptyIndex();

    const parsed = JSON.parse(raw) as Partial<ConversationIndex> | null;
    if (!parsed || !Array.isArray(parsed.conversations)) return emptyIndex();

    return {
      version: typeof parsed.version === "number" ? parsed.version : CONVERSATION_STORE_VERSION,
      conversations: parsed.conversations.filter(
        (meta): meta is ConversationMeta => typeof meta?.id === "string"
      ),
    };
  } catch {
    return emptyIndex();
  }
}

function writeIndex(storage: StorageLike, index: ConversationIndex): StoreResult {
  try {
    storage.setItem(CONVERSATION_INDEX_KEY, JSON.stringify(index));
    return { ok: true };
  } catch (error) {
    return failure(error);
  }
}

function writeEnvelope(
  storage: StorageLike,
  id: string,
  envelope: Omit<ConversationEnvelope, "version">
): StoreResult {
  try {
    storage.setItem(
      conversationKey(id),
      JSON.stringify({ version: CONVERSATION_STORE_VERSION, ...envelope })
    );
    return { ok: true };
  } catch (error) {
    return failure(error);
  }
}

/**
 * The browser's localStorage, or null when it is unreachable. Touching `window.localStorage` is
 * itself what throws when site data is blocked for the origin, so the access is inside the try.
 */
export function browserStorage(): StorageLike | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function newConversationId(): string {
  return `con_${crypto.randomUUID().replace(/-/g, "")}`.substring(0, 30);
}

/**
 * Title from the first user message: whitespace collapsed and trimmed, `New chat` when empty,
 * truncated as 97 chars + "..." — mirroring the backend's TruncateTitle.
 */
export function deriveTitle(firstUserMessage: string): string {
  const collapsed = firstUserMessage.replace(/\s+/g, " ").trim();
  if (!collapsed) return "New chat";
  if (collapsed.length <= TITLE_MAX_LENGTH) return collapsed;
  return `${collapsed.substring(0, TITLE_MAX_LENGTH - 3)}...`;
}

/** Most-recently-updated first. One ordering rule, applied on read; writers upsert in place. */
export function listConversations(storage: StorageLike): ConversationMeta[] {
  return [...readIndex(storage).conversations].sort((a, b) =>
    a.updated_at === b.updated_at ? 0 : a.updated_at > b.updated_at ? -1 : 1
  );
}

export function loadConversation(storage: StorageLike, id: string): ConversationEnvelope | null {
  try {
    // Index-first, the same rule saveConversation and deleteConversation apply: the index is the
    // sole record of existence. An envelope the index does not carry is an orphan left by a partial
    // write — a failed index write after createConversation, or a failed removeItem after delete —
    // and must not read as a conversation. Returning it would open a chat with no sidebar row and
    // no title whose every save is then refused as unknown, losing follow-up messages on reload.
    if (!readIndex(storage).conversations.some((meta) => meta.id === id)) return null;

    const raw = storage.getItem(conversationKey(id));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<ConversationEnvelope> | null;
    if (!parsed || !Array.isArray(parsed.messages)) return null;

    return {
      version: typeof parsed.version === "number" ? parsed.version : CONVERSATION_STORE_VERSION,
      messages: parsed.messages.map((message) => ({
        ...message,
        toolCalls: message.toolCalls ?? [],
      })),
      traffic_context: parsed.traffic_context ?? {},
    };
  } catch {
    return null;
  }
}

export function createConversation(
  storage: StorageLike,
  conversation: {
    id: string;
    title: string;
    messages: ChatMessage[];
    traffic_context: TrafficContext;
  }
): StoreResult {
  const { id, title, messages, traffic_context } = conversation;

  const written = writeEnvelope(storage, id, { messages, traffic_context });
  if (!written.ok) return written;

  const now = new Date().toISOString();
  const index = readIndex(storage);
  const meta: ConversationMeta = { id, title, created_at: now, updated_at: now };

  return writeIndex(storage, {
    version: CONVERSATION_STORE_VERSION,
    conversations: [meta, ...index.conversations.filter((existing) => existing.id !== id)],
  });
}

export function saveConversation(
  storage: StorageLike,
  id: string,
  payload: { messages: ChatMessage[]; traffic_context: TrafficContext }
): StoreResult {
  // Index-first, before any write: an id the index does not carry no longer exists, so a stream
  // frame that lands after a delete cannot recreate its envelope.
  const index = readIndex(storage);
  const meta = index.conversations.find((existing) => existing.id === id);
  if (!meta) return { ok: false, error: "unknown conversation" };

  const written = writeEnvelope(storage, id, payload);
  if (!written.ok) return written;

  return writeIndex(storage, {
    version: CONVERSATION_STORE_VERSION,
    conversations: index.conversations.map((existing) =>
      existing.id === id ? { ...existing, updated_at: new Date().toISOString() } : existing
    ),
  });
}

export function deleteConversation(storage: StorageLike, id: string): StoreResult {
  // Index first again: revoking existence before dropping the payload means a failure here leaves
  // an orphaned envelope rather than a resurrectable conversation.
  const index = readIndex(storage);
  const removed = writeIndex(storage, {
    version: CONVERSATION_STORE_VERSION,
    conversations: index.conversations.filter((existing) => existing.id !== id),
  });
  if (!removed.ok) return removed;

  try {
    storage.removeItem(conversationKey(id));
    return { ok: true };
  } catch (error) {
    return failure(error);
  }
}

/**
 * Migrate the pre-upgrade `help-chat-messages` array into a conversation.
 *
 * Ordering is the whole contract: a non-array or unreadable value is left untouched; an empty
 * array is removed (nothing can be lost) and reported as no migration; a non-empty array is
 * written, **read back** — envelope present with the same message count *and* the id carried by
 * the index — and only then is the legacy key removed. A failed write leaves the legacy key
 * intact; a failed removal after a successful write reports `legacy_cleared: false`, so the data
 * is safe and the next load simply re-migrates.
 */
/**
 * A parseable legacy array can still hold junk, and the two failure shapes differ: `[null]` throws
 * outright on `message.toolCalls`, while `[3]` and `["x"]` normalize silently into role-less,
 * content-less messages. Both are checked here rather than defended against downstream.
 */
function isLegacyMessage(value: unknown): value is ChatMessage {
  if (typeof value !== "object" || value === null) return false;
  const message = value as Partial<ChatMessage>;
  return (
    typeof message.id === "string" &&
    (message.role === "user" || message.role === "assistant") &&
    typeof message.content === "string" &&
    (message.toolCalls === undefined || Array.isArray(message.toolCalls))
  );
}

export function migrateLegacyConversation(
  storage: StorageLike,
  traffic_context: TrafficContext
): MigrationResult {
  let raw: string | null;
  try {
    raw = storage.getItem(LEGACY_MESSAGES_KEY);
  } catch {
    return { migrated: false };
  }
  if (raw === null) return { migrated: false };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Unreadable: leave it in place rather than destroying something we cannot interpret.
    return { migrated: false };
  }
  if (!Array.isArray(parsed)) return { migrated: false };

  if (parsed.length === 0) {
    try {
      storage.removeItem(LEGACY_MESSAGES_KEY);
    } catch {
      // Nothing can be lost from an empty array.
    }
    return { migrated: false };
  }

  // Validate every element before writing anything. AC 2 requires that no messages are lost, so a
  // partial migration is the wrong answer: one malformed element aborts the whole migration and
  // leaves the legacy key untouched for inspection, rather than throwing out of the landing effect
  // (which sets its once-guard first, so a throw there would also skip refreshList and navigation).
  if (!parsed.every(isLegacyMessage)) return { migrated: false };

  const messages: ChatMessage[] = parsed.map((message) => ({
    ...message,
    toolCalls: message.toolCalls ?? [],
  }));
  const id = newConversationId();
  const firstUserMessage = messages.find((message) => message.role === "user");

  const created = createConversation(storage, {
    id,
    title: deriveTitle(firstUserMessage?.content ?? ""),
    messages,
    traffic_context,
  });
  // The write failed, but `messages` is already validated and normalized: hand it back so the
  // landing can show it rather than dropping the user on an empty greeting with their transcript
  // stranded behind a key nothing reads.
  if (!created.ok) return { migrated: false, recovered: messages };

  const envelope = loadConversation(storage, id);
  const indexed = listConversations(storage).some((meta) => meta.id === id);
  if (!envelope || envelope.messages.length !== messages.length || !indexed) {
    return { migrated: false, recovered: messages };
  }

  let legacy_cleared = true;
  try {
    storage.removeItem(LEGACY_MESSAGES_KEY);
  } catch {
    legacy_cleared = false;
    // The conversation is verified persisted above, so the legacy array is now redundant — and
    // leaving it intact is actively harmful: the next mount would migrate the same messages under
    // a fresh id, adding one duplicate conversation per load. Overwrite it with an empty array so
    // the next run takes the empty-array path and migrates nothing. Reaching this point means
    // writes work (createConversation succeeded), so this normally lands even when removeItem did
    // not; if it too fails there is nothing further that is safe to try.
    try {
      storage.setItem(LEGACY_MESSAGES_KEY, "[]");
    } catch {
      // Both the removal and the overwrite failed.
    }
  }

  return { migrated: true, id, legacy_cleared };
}
